import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { CheckCircle, XCircle, Loader2, Mail } from 'lucide-react';
import { toast } from 'sonner';

export default function VerifyEmail() {
  const { t } = useTranslation();
  const [status, setStatus] = useState('verifying');
  const [message, setMessage] = useState('');
  const [searchParams] = useSearchParams();
  const { verifyEmail } = useAuth();
  const navigate = useNavigate();
  const token = searchParams.get('token');

  useEffect(() => {
    const verify = async () => {
      if (!token) {
        setStatus('error');
        setMessage(t('auth.verifyFail'));
        return;
      }

      try {
        const result = await verifyEmail(token);
        setStatus('success');
        setMessage(result.message || t('auth.verifySuccess'));
        toast.success(t('auth.verifySuccess'));
      } catch (error) {
        setStatus('error');
        setMessage(error.response?.data?.detail || t('auth.verifyFail'));
      }
    };

    verify();
  }, [token, verifyEmail, t]);

  return (
    <div className="min-h-screen bg-[#0B1120] flex items-center justify-center p-4">
      <div className="max-w-md w-full bg-[#121A2F] rounded-lg shadow-lg p-8 text-center">
        {status === 'verifying' && (
          <>
            <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <Loader2 className="w-8 h-8 text-[#F59E0B] animate-spin" />
            </div>
            <h1 className="text-2xl font-bold text-white mb-2">{t('auth.verifying')}</h1>
            <p className="text-[#94A3B8]">{t('common.loading')}</p>
          </>
        )}

        {status === 'success' && (
          <>
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="w-8 h-8 text-green-600" />
            </div>
            <h1 className="text-2xl font-bold text-white mb-2">{t('auth.verifySuccess')}</h1>
            <p className="text-[#94A3B8] mb-6">{message}</p>
            <Button onClick={() => navigate('/auth')} data-testid="verify-go-to-login-button" className="w-full bg-[#F59E0B] hover:bg-[#D97706]">
              {t('auth.loginBtn')}
            </Button>
          </>
        )}

        {status === 'error' && (
          <>
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <XCircle className="w-8 h-8 text-red-600" />
            </div>
            <h1 className="text-2xl font-bold text-white mb-2">{t('auth.verifyFail')}</h1>
            <p className="text-[#94A3B8] mb-6">{message}</p>
            <div className="space-y-3">
              <Button onClick={() => navigate('/auth')} data-testid="verify-go-to-login-button" className="w-full bg-[#F59E0B] hover:bg-[#D97706]">
                {t('auth.loginBtn')}
              </Button>
              <Link to="/resend-verification" data-testid="resend-verification-link" className="text-[#F59E0B] hover:text-amber-700 text-sm flex items-center justify-center">
                <Mail className="w-4 h-4 mr-1" />
                {t('auth.resendEmail')}
              </Link>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
